import { TILE } from '../sim/constants';
import { dist2 } from '../lib/intmath';
import { deriveRng, nextInt } from '../sim/rng';
import type { RngState } from '../sim/rng';
import { BUILDING_BY_NAME, buildingDef } from '../content/buildings';
import { UNIT_BY_NAME, unitDef } from '../content/units';
import { difficultyDef } from '../content/difficulties';
import type { DifficultyDef, DifficultyId } from '../content/difficulties';
import {
    ProductionKind,
    buildQueueOf,
    canPlace,
    costOf,
    powerOf,
    prerequisitesMet,
} from '../sim/build';
import { YARD_QUEUE, queueOfUnit } from '../content/production';
import { getMap, startRotation } from '../content/maps';
import type { MapId } from '../content/maps';
import { UnitMode } from '../sim/types';
import type { Building, GameState, Unit } from '../sim/types';
import { isWalkable } from '../sim/path';
import type { Grid } from '../sim/path';
import type { CommandDraft } from '../sim/commands';

/*
 * המוח של היריב.
 *
 * **פונקציה טהורה של המצב.** אין לה זיכרון משלה ואין לה אקראיות חופשית: כל הגרלה
 * יוצאת מ-`deriveRng(seed, player, tick)` שהוא חסר-מצב. לכן אפשר לשמור משחק
 * באמצע, לטעון אותו, ולקבל בדיוק את אותו יריב — היריב אינו חלק מהמצב השמור,
 * הוא נגזר ממנו. אותה תכונה בדיוק היא מה שיאפשר בעתיד להחליף שחקן AI בשחקן רשת
 * בלי לגעת בשום דבר אחר.
 *
 * **ה-AI אינו מרמה.** הוא פועל דרך אותן פקודות שהשחקן שולח, ועובר את אותה בדיקת
 * `isLegal` ואת אותם תנאי-קדם. אם הוא לא יכול להרשות לעצמו מבנה — הוא לא בונה אותו.
 *
 * **אין לו השהיית קלט.** לשחקן יש INPUT_DELAY_TICKS (100ms) כי הפקודות שלו יעברו
 * יום אחד ברשת; ל-AI אין רשת. במקום זה יש לו השהיה גדולה בהרבה וגלויה —
 * `decisionIntervalTicks`, שנייה וחצי ברמה רגילה. זה גם הוגן וגם מה ששומר על
 * "המשך משחק" מדויק: אין פקודות תלויות באוויר ברגע השמירה.
 */

/** סדר הבנייה. אחריו ה-AI מרחיב בלולאה — עוד כוח, עוד הכנסה, עוד הגנה. */
const BUILD_ORDER: readonly (keyof typeof BUILDING_BY_NAME)[] = [
    'power',
    'barracks',
    'refinery',
    'power',
    'factory',
    'pillbox',
    'power',
    'gunTurret',
    /*
     * **נ"מ לפני מנחת.** היריב חייב להיות מסוגל לענות למסוקים לפני שהוא בונה
     * משלו, אחרת התוקף הראשון מנצח וההגנה מגיעה תמיד באיחור של תור אחד.
     */
    'aaGun',
    'airfield',
];

/**
 * תקרה לכל מבנה שאינו בסדר הבנייה.
 *
 * **בלי תקרה ה-AI מתבצר לנצח.** `simBatch` מדד: 40 מבנים מול 12 יחידות, אפס
 * אשראי, ואף משחק לא הוכרע ב-12,000 טיקים. כל שקל הלך לעוד מגדל. כשהבסיס
 * מגיע לתקרה, `nextBuilding` מחזיר null וכל האשראי זורם ליחידות — וזה מה
 * שהופך את היריב לתוקף במקום לצב.
 *
 * תחנת הכוח אינה ברשימה: היא נבנית לפי צורך אמיתי ולא לפי מכסה.
 */
const BUILDING_CAP: readonly {
    name: keyof typeof BUILDING_BY_NAME;
    max: number;
    /** האם התקרה גדלה עם `economyBonus` (מייצר) או עם `defenseBonus` (עומד). */
    economy: boolean;
}[] = [
    // מפעל עיבוד שני הוא הכפלת ההכנסה — הוא קודם לכל הגנה.
    { name: 'refinery', max: 2, economy: true },
    /*
     * מפעל וקסרקטין שניים **מזרזים את הייצור** (ראו `advanceQueue`), ולכן הם
     * השקעה ולא כפילות. בלעדיהם ה-AI צובר אשראי שאין לו דרך להוציא.
     */
    { name: 'factory', max: 2, economy: true },
    { name: 'barracks', max: 2, economy: true },
    /*
     * ‏4+3 הורדו ל-3+2 בכיוון של מלכודת 31: התקרה קיימת כדי להעביר כסף ליחידות,
     * וכשהיא גבוהה מדי היא מחזירה את ההתבצרות מדלת אחורית.
     *
     * **אבל השינוי הזה לבדו נמדד כחסר השפעה מוחלטת** — התוצאות יצאו זהות
     * ביט-לביט. הסיבה: ה-AI כלל לא הגיע לתקרות. הוא בנה **אחד מכל סוג** ותו לא,
     * כי כל שקל הלך ליחידה הזולה ביותר שאפשר להרשות עכשיו. מה שבאמת פתח את זה
     * הוא החיסכון ב-`nextUnit`; רק אחריו הבסיסים גדלו ל-11-18 מבנים, ורק אז
     * לתקרות האלה יש בכלל משמעות.
     *
     * הנ"מ נשאר על 3 — הוא חסר תועלת מוחלט מול קרקע ואינו משתתף בקיפאון.
     */
    { name: 'pillbox', max: 3, economy: false },
    { name: 'gunTurret', max: 2, economy: false },
    { name: 'aaGun', max: 3, economy: false },
    // מנחת אחד מספיק: הוא פותח תור ייצור שלם, לא מזרז אותו.
    { name: 'airfield', max: 1, economy: true },
];

/** מרווח חשמל שמתחתיו בונים תחנה נוספת. אפס היה אומר לבנות רק אחרי שכבר מאוחר. */
const POWER_HEADROOM = 50;

/** הרכב הכוח: על כל טנק שני חיילים. זול, ומכסה את שני סוגי השריון. */
/*
 * התערובת. המסוק נדיר בכוונה: הוא יקר, והוא **הציר השני** ולא היחידה הבאה —
 * כוח שכולו מסוקים מת מול תותח נ"מ אחד.
 */
const UNIT_MIX: readonly (keyof typeof UNIT_BY_NAME)[] = [
    'gi',
    'gi',
    'bazooka',
    'grizzly',
    'gunship',
];

/*
 * **`hound` אינו בתערובת בכוונה.** ‏`freeArmy` מסננת `weapon !== null`, ולכן
 * כלב סיור שהיריב ייצר לא יקבל לעולם פקודה: הוא יישב בבסיס ויבזבז אשראי.
 * יחידה בלי נשק דורשת התנהגות משלה ב-AI (סיור), ולא סתם שורה בתערובת.
 */

/*
 * **מה שהיה קבוע גלובלי הוא היום שדה ברמת הקושי.**
 *
 * מספר הקוצרים, עומק התור, שומרי הבסיס, גדילת הגל והתקרות — כולם היו זהים
 * בשלוש הרמות, ולכן "קשה" נבדל מ"רגיל" רק בקצב ההחלטה ובאחוז הדילוג. יריב
 * שמחליט מהר יותר על אותה כלכלה בדיוק אינו יריב חזק יותר; הוא אותו יריב
 * ממהר. ראו `content/difficulties`.
 */

/** עד כמה רחוק מהמבנים נחשב אויב "בתוך הבסיס" ומצדיק הגנה מיידית. */
const DEFENSE_RADIUS = 12 * TILE;

/**
 * כל כמה טיקים הגל גדל ביחידה אחת, ועד כמה.
 *
 * **בלי זה המשחק לא נגמר.** simBatch מדד שני AI על המפה הגדולה במשך 7,000 טיקים
 * ואף משחק לא הוכרע: גל של שלוש-שש יחידות פשוט אינו שובר בסיס עם פילבוקס ומגדל.
 * ב-RA2 הגלים גדלים ככל שהמשחק מתקדם, וזה מה שהופך משחק ארוך למשחק שמסתיים.
 */
/*
 * **הקצב והתקרה עברו לרמת הקושי.** האזהרה נשארת נכונה במלואה: סף שגדל מהר
 * מקצב הייצור מבטל את ההתקפה לגמרי (מלכודת 32), ולכן `maxWaveBonus` הגבוה של
 * רמה קשה מלווה בכלכלה שמסוגלת לממן אותו — שישה קוצרים ותור עמוק. תקרה בלי
 * כלכלה היא מספר שהיריב לעולם לא יגיע אליו.
 */

/** מתחת לאחוז הזה מה-hp, מבנה נכנס לתיקון. שלמים: `hp * 100 < max * REPAIR_AT`. */
const REPAIR_AT = 90;

/** רזרבה שלא נוגעים בה לתיקונים — קודם כול ממשיכים לייצר. */
const REPAIR_RESERVE = 400;

/** כל כמה טיקים כלב הסיור בוחר יעד חדש גם אם עוד לא הגיע. */
const SCOUT_REPATH_TICKS = 600;

export function decide(state: GameState, grid: Grid, player: number): CommandDraft[] {
    const me = state.players[player];
    if (!me || me.defeated) return [];

    /*
     * רמת הקושי וה-seed נקראים **מהמצב**, לא מפרמטרים. זה מה שהופך את "המשך משחק"
     * למדויק גם ליריב: אין שום ערך שצריך לשחזר בצד, כי אין שום ערך בצד.
     */
    const difficulty = difficultyDef(me.difficulty as DifficultyId);
    if (state.tick % difficulty.decisionIntervalTicks !== 0) return [];

    const myBuildings = state.buildings.filter((b) => b.owner === player && b.hp > 0);
    if (myBuildings.length === 0) return lastStand(state, grid, player);

    const rng = deriveRng(state.seed, player, state.tick);

    /*
     * "טעות" היא דילוג על ההחלטה כולה, לא בחירה גרועה. זה מה שגורם לרמה קלה
     * להרגיש **איטית** — יריב שמפגר אחריך — ולא **טיפשה**, יריב שעושה שטויות.
     */
    if (difficulty.mistakePct > 0 && nextInt(rng, 100) < difficulty.mistakePct) return [];

    const commands: CommandDraft[] = [];

    // ---- מבנה שהושלם ממתין להצבה. הוא חוסם את התור, ולכן הוא קודם לכול.
    const readyBuilding = buildQueueOf(me)[0];
    if (readyBuilding?.ready) {
        const spot = findSpot(state, grid, player, readyBuilding.type);
        if (spot) return [{ kind: 'place', unitIds: [], tileX: spot.x, tileY: spot.y }];
        /*
         * אין מקום חוקי בכל טווח הבנייה. ביטול מחזיר בדיוק את מה ששולם ומשחרר
         * את התור — עדיף מ-AI שקופא לנצח עם מבנה שאין לו איפה לשים.
         */
        return [{ kind: 'cancel', unitIds: [], queueIndex: YARD_QUEUE }];
    }

    /*
     * ---- תיקון מבנים
     *
     * **מה שכל שחקן אנושי עושה, וה-AI לא עשה מעולם.** מגדל שירד ל-40% נשאר כך
     * עד שנפל, ולכן ההגנה של היריב נשחקה בכל גל בלי שהוא ישלם דבר כדי להחזיק
     * אותה. התיקון עולה ~1 אשראי לטיק למבנה (`REPAIR_COST_DIVISOR`), כלומר הוא
     * אכן מתחרה בייצור — ומכאן הרזרבה: קודם כול ממשיכים לבנות צבא.
     *
     * הפקודה היא **מתג**, ולכן מי שכבר בתיקון מדלג. הכיבוי אינו נדרש: הסימולציה
     * מכבה מעצמה כשה-hp מלא.
     */
    if (difficulty.repairs && me.credits > REPAIR_RESERVE) {
        for (const building of myBuildings) {
            if (building.repairing === 1) continue;
            if (building.hp * 100 >= buildingDef(building.type).hp * REPAIR_AT) continue;
            commands.push({ kind: 'repair', unitIds: [], buildingId: building.id });
        }
    }

    // ---- הזמנת מבנה
    /**
     * **חוסכים למבנה הבא בדיוק כמו שחוסכים ליחידה יקרה.**
     *
     * ‏`economyBonus` העלה את התקרות של רמה קשה — ולא שינה כלום, כי היריב
     * מעולם לא הגיע אליהן. נמדד: במשחק מראה רמה **קלה** סיימה עם 21 מבנים מול
     * ‏16.5 לקשה, ומול שחקן פסיבי 18.3 מול 13.3. הסיבה אינה התקרה אלא הארנק:
     * מבנה עולה 800-2,000, חייל עולה 100, ותור עמוק בולע כל שקל ברגע שהוא
     * נכנס — כך שהיתרה כמעט לעולם אינה מגיעה למחיר של מבנה.
     *
     * זו בדיוק מלכודת 67 בפעם השנייה: מספר שהועלה בלי שנבדק אם התנאים שבהם
     * הוא נושך מתקיימים בכלל.
     */
    const wantedBuilding = buildQueueOf(me).length === 0 ? nextBuilding(myBuildings, player, difficulty) : null;
    let savingForBuilding = false;
    if (wantedBuilding !== null) {
        if (affordable(ProductionKind.Building, wantedBuilding, me.credits, myBuildings, player)) {
            commands.push({
                kind: 'queue',
                unitIds: [],
                itemKind: ProductionKind.Building,
                itemType: wantedBuilding,
            });
        } else if (
            prerequisitesMet(
                { kind: ProductionKind.Building, type: wantedBuilding },
                myBuildings,
                player,
            ) &&
            me.credits * 2 >= buildingDef(wantedBuilding).cost
        ) {
            /*
             * חצי מהמחיר כבר בכיס — עוצרים את הזמנת היחידות להחלטה אחת. הסף
             * שמרני יותר מזה של היחידות (שני שלישים) כי מבנה יקר בהרבה, ועצירה
             * ארוכה מדי היא צבא שלא נבנה.
             */
            savingForBuilding = true;
        }
    }

    /*
     * ---- הזמנת יחידות: **ממלאים את התור, לא פריט אחד להחלטה.**
     *
     * זה היה החנק שנמדד. ברמה קלה ההחלטה היא כל 60 טיקים ועוד 45% דילוג, כלומר
     * החלטה אחת ל-109 טיקים בערך; במחיר ממוצע של ~366 זה **3.4 אשראי לטיק
     * שהוזמנו מול 20 שאפשר לנקז.** התור פשוט היה ריק רוב הזמן, והיריב סיים
     * משחקים עם 10-32 אלף אשראי שלא נגע בהם ובלי צבא שיכול לשבור בסיס.
     *
     * **`budget` הוא הלב של התיקון.** ‏`affordable` בודק מול היתרה הנוכחית, ולכן
     * לולאה תמימה הייתה מזמינה ארבעה טנקים כשיש כסף לאחד — והראשון היה חוסם את
     * התור עד שיצטבר אשראי, כלומר הופך את הבעיה לגרועה יותר.
     */
    let budget = me.credits;
    for (const command of commands) {
        if (command.kind === 'queue') budget -= costOf({ kind: command.itemKind, type: command.itemType });
    }
    /*
     * **תור לכל מבנה ייצור.** הקסרקטין והמפעל עצמאיים, ולכן היריב ממלא כל אחד
     * מהם עד העומק שלו — בדיוק כמו שחקן שמזמין חי"ר וטנקים במקביל.
     */
    const queueLoad = me.queues.map((queue) => queue.length);
    /*
     * **מה שכבר בתור נספר כאילו כבר קיים.**
     *
     * ‏`nextUnit` השוותה מול יחידות **חיות** בלבד, וזה עבד במקרה: קוצר עולה
     * ‏1,400 ולכן התקציב עצר את הלולאה אחרי אחד. כלב סיור עולה 150 — נמדד
     * שהיריב הזמין שבעה כשביקשנו שניים, כי אף אחד מהם עוד לא נולד. הספירה
     * הנכונה היא "כמה יהיו לי", לא "כמה יש לי עכשיו".
     */
    const pending = new Map<number, number>();
    for (const queue of me.queues) {
        for (const item of queue) {
            if (item.kind !== ProductionKind.Unit) continue;
            pending.set(item.type, (pending.get(item.type) ?? 0) + 1);
        }
    }
    for (let attempt = 0; !savingForBuilding && attempt < difficulty.queueDepth * 2; attempt++) {
        const type = nextUnit(state, player, myBuildings, budget, rng, difficulty, pending);
        if (type === null) break;
        const queue = queueOfUnit(type);
        if (queue < 0 || (queueLoad[queue] ?? 0) >= difficulty.queueDepth) break;
        queueLoad[queue] = (queueLoad[queue] ?? 0) + 1;
        pending.set(type, (pending.get(type) ?? 0) + 1);
        budget -= costOf({ kind: ProductionKind.Unit, type });
        commands.push({
            kind: 'queue',
            unitIds: [],
            itemKind: ProductionKind.Unit,
            itemType: type,
        });
    }

    /*
     * ---- סיור
     *
     * **היחידה היחידה שרואה 11 אריחים ואינה יורה בכלל.** ‏`freeArmy` מסננת
     * `weapon !== null` (מלכודת 54), ולכן כלב סיור לא היה מקבל שום פקודה ורק
     * מבזבז אשראי — וזו הסיבה ש-`hound` היה בתוכן ומחוץ למשחק.
     *
     * ההתנהגות היא **חסרת מצב**, כמו כל השאר: הכלב שולח את עצמו אל נקודת
     * הפתיחה **הרחוקה ביותר** מהמקום שבו הוא עומד. כשהוא מגיע, אותה נוסחה
     * מצביעה על נקודה אחרת — ומזה נולד מסלול הלוך-ושוב בין בסיסי היריבים,
     * שחושף בדיוק את מה ש-`enemyTarget` צריך כדי לדרג לפי חולשה ולא לפי מרחק.
     * ‏`SCOUT_REPATH_TICKS` מוציא כלב שנתקע מלולאה בלי להוסיף לו זיכרון.
     */
    for (const scout of scoutsOf(state, player)) {
        const stuck = state.tick % SCOUT_REPATH_TICKS === 0;
        if (scout.mode !== UnitMode.Idle && !stuck) continue;
        const goal = farthestStart(state, player, scout.x, scout.y);
        if (!goal) continue;
        const spot = reachableTile(grid, goal.x, goal.y);
        if (spot) {
            commands.push({ kind: 'move', unitIds: [scout.id], tileX: spot.x, tileY: spot.y });
        }
    }

    // ---- הגנה והתקפה
    const army = freeArmy(state, player);
    const threat = nearestThreat(state, player, myBuildings);

    if (threat && army.length > 0) {
        // אויב בתוך הבסיס: כל מי שפנוי יוצא אליו, בלי לחכות לגודל גל.
        const spot = reachableTile(grid, threat.x, threat.y);
        if (spot) {
            commands.push({ kind: 'attackMove', unitIds: army, tileX: spot.x, tileY: spot.y });
            return commands;
        }
    }

    /*
     * לפני `firstAttackTick` הוא בונה ולא תוקף. ההגנה למעלה כן פעילה מהשנייה
     * הראשונה — "שקט" הוא לא "פתוח לרווחה".
     */
    if (state.tick < difficulty.firstAttackTick) return commands;

    const wanted =
        difficulty.waveSize +
        Math.min(
            difficulty.maxWaveBonus,
            Math.floor((state.tick - difficulty.firstAttackTick) / difficulty.waveGrowthTicks),
        );
    if (army.length >= wanted + difficulty.homeGuard) {
        const wave = army.slice(difficulty.homeGuard);
        const target = enemyTarget(state, player, myBuildings);
        const spot = target ? reachableTile(grid, target.x, target.y) : null;
        if (spot) {
            commands.push({ kind: 'attackMove', unitIds: wave, tileX: spot.x, tileY: spot.y });
        }
    }

    return commands;
}

/** אין בסיס — מה שנשאר יוצא לחפש את האויב. אין מה להגן עליו יותר. */
function lastStand(state: GameState, grid: Grid, player: number): CommandDraft[] {
    const army = freeArmy(state, player);
    if (army.length === 0) return [];
    const target = enemyTarget(state, player, []);
    const spot = target ? reachableTile(grid, target.x, target.y) : null;
    if (!spot) return [];
    return [{ kind: 'attackMove', unitIds: army, tileX: spot.x, tileY: spot.y }];
}

/** יחידות הקרב שאינן כבר בדרך לקרב. ממוין לפי מזהה — סדר קבוע בין הרצות. */
function freeArmy(state: GameState, player: number): number[] {
    return state.units
        .filter(
            (unit) =>
                unit.owner === player &&
                unitDef(unit.type).weapon !== null &&
                unit.mode !== UnitMode.AttackMove &&
                unit.mode !== UnitMode.Attacking,
        )
        .map((unit) => unit.id)
        .sort((a, b) => a - b);
}

/**
 * יחידות סיור: **בלי נשק ובלי מטען.** שתי התכונות יחד מבודדות את הכלב מהקוצר,
 * ובלי לכתוב את שמו — אותו עיקרון שבזכותו פילבוקס אינו מערכת נפרדת.
 */
function scoutsOf(state: GameState, player: number): Unit[] {
    return state.units
        .filter((unit) => {
            if (unit.owner !== player || unit.hp <= 0) return false;
            const def = unitDef(unit.type);
            return def.weapon === null && def.capacity === 0;
        })
        .sort((a, b) => a.id - b.id);
}

/**
 * נקודת הפתיחה **הרחוקה ביותר** מנקודה נתונה — כולל זו של הסייר עצמו.
 *
 * נקודת פתיחה היא מידע ציבורי בכל RTS — גם שחקן אנושי יודע שהיריב במשבצת
 * הנגדית — ולכן שליחת סיור אליה אינה ריגול. מה שהכלב **יגלה** בדרך הוא כן
 * ידע חדש, וזה כל העניין.
 *
 * **הבסיס שלנו נספר ברשימה בכוונה, וזה מה שהופך את זה לסיור.** הגרסה
 * הראשונה בחרה רק בין בסיסי היריבים, ובמפה של שניים יש בדיוק אחד: הכלב הלך
 * לשם, הגיע, קיבל שוב את אותו יעד, נשאר עומד בבסיס האויב ומת. נמדד — אפס
 * שיפור בשטח שנחשף. עכשיו היעד מתחלף מאליו בכל הגעה, ומזה נולד מסלול
 * הלוך-ושוב על הציר שממנו מגיעות ההתקפות.
 */
function farthestStart(
    state: GameState,
    player: number,
    worldX: number,
    worldY: number,
): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestDistance = -1;
    for (const [index, slot] of state.startSlots.entries()) {
        if (index !== player && state.players[index]?.defeated) continue;
        const d2 = dist2(worldX, worldY, slot.x * TILE + TILE / 2, slot.y * TILE + TILE / 2);
        // שבירת שוויון לפי אינדקס — סדר קבוע בכל מחשב ובכל הרצה.
        if (d2 <= bestDistance) continue;
        bestDistance = d2;
        best = { x: slot.x, y: slot.y };
    }
    return best;
}

function affordable(
    kind: number,
    type: number,
    credits: number,
    buildings: readonly Building[],
    player: number,
): boolean {
    const item = { kind, type };
    if (!prerequisitesMet(item, buildings, player)) return false;
    /*
     * דורש את מלוא העלות לפני שמזמינים, למרות שהניקוז הדרגתי. פריט יקר שנכנס
     * לתור בלי כיסוי היה יושב בראשו וחוסם את כל מה שאחריו עד שיצטבר אשראי.
     */
    return credits >= costOf(item);
}

/**
 * המבנה הבא, או `null` אם הבסיס שלם — ואז כל האשראי הולך ליחידות.
 * **ההחזרה של null היא הפיצ'ר**, לא מקרה קצה: היא מה שמסיים את שלב הבנייה.
 */
export function nextBuilding(
    myBuildings: readonly Building[],
    player: number,
    difficulty: DifficultyDef,
): number | null {
    const owned = myBuildings.map((b) => buildingDef(b.type).id);

    for (const name of BUILD_ORDER) {
        if (!owned.includes(name)) return BUILDING_BY_NAME[name];
    }

    // חשמל לפי צורך: מרווח קטן מדי מאט את כל הייצור ומחליש את ההגנות.
    const power = powerOf(myBuildings, player);
    if (power.produced - power.consumed < POWER_HEADROOM) return BUILDING_BY_NAME.power;

    // ואז מכסות. הראשון ברשימה שעדיין מתחת לתקרה — הסדר הוא סדר העדיפות.
    for (const entry of BUILDING_CAP) {
        const max = entry.max + (entry.economy ? difficulty.economyBonus : difficulty.defenseBonus);
        if (owned.filter((id) => id === entry.name).length < max) {
            return BUILDING_BY_NAME[entry.name];
        }
    }
    return null;
}

/**
 * מה לייצר. קוצרים קודם — בלי הכנסה אין צבא — ואז תערובת הכוח.
 * מה שאי אפשר להזמין נופל למועמד הבא, ולא עוצר את הייצור כולו.
 */
function nextUnit(
    state: GameState,
    player: number,
    myBuildings: readonly Building[],
    credits: number,
    rng: RngState,
    difficulty: DifficultyDef,
    /** כמה מכל סוג כבר בתור. יחידה שהוזמנה נספרת כאילו כבר נולדה. */
    pending: ReadonlyMap<number, number>,
): number | null {
    const candidates: number[] = [];
    const owned = (type: number, live: number) => live + (pending.get(type) ?? 0);

    const harvesters = state.units.filter(
        (u) => u.owner === player && unitDef(u.type).capacity > 0,
    ).length;
    if (owned(UNIT_BY_NAME.harvester, harvesters) < difficulty.harvesters) {
        candidates.push(UNIT_BY_NAME.harvester);
    }

    /*
     * **כלב סיור, מיד אחרי הקוצרים.** הוא עולה 150 — פחות מחייל וחצי — ומה
     * שהוא מחזיר הוא הדבר היחיד שהופך את שקלול היעדים ל-`enemyTarget` למשהו:
     * ידיעה על מה שמעבר לכיוון שאליו כבר שלחנו גלים. בלעדיו היריב מדרג לפי
     * מרחק בלבד, ומזה נולד המעגל הקבוע שנמדד ב-FFA.
     */
    if (owned(UNIT_BY_NAME.hound, scoutsOf(state, player).length) < difficulty.scouts) {
        candidates.push(UNIT_BY_NAME.hound);
    }

    /*
     * ההגרלה בוחרת את **נקודת הפתיחה** בתערובת ולא יחידה בודדת, וכל השאר נבדקות
     * אחריה בסדר. כך התערובת נשמרת לאורך זמן, אבל החלטה בודדת אף פעם לא נופלת
     * רק מפני שהוגרלה יחידה שאי אפשר לייצר עכשיו.
     */
    const offset = nextInt(rng, UNIT_MIX.length);
    for (let i = 0; i < UNIT_MIX.length; i++) {
        const name = UNIT_MIX[(offset + i) % UNIT_MIX.length];
        if (name !== undefined) candidates.push(UNIT_BY_NAME[name]);
    }

    /*
     * **חוסכים ליחידה שהוגרלה כשהיא כמעט בהישג יד.**
     *
     * בלי זה כל שקל הלך ליחידה הזולה ביותר שאפשר להרשות **עכשיו**, וזה מדד
     * כטבח: ‏21-24 חי"ר מול 1-3 טנקים בכל בסיס אחרי עשר דקות — כלומר צבא שכולו
     * הסוג שפילבוקס קוטל, ומזה נבע שאף משחק לא הוכרע. גם המבנה השני לא נבנה
     * אף פעם, כי האשראי לא הספיק להצטבר ל-2,000.
     *
     * הסף הוא שני שלישים מהמחיר: קרוב מספיק כדי שההמתנה תהיה קצרה, רחוק מספיק
     * כדי שלא נקפא כשבאמת אין כסף. **מתמטיקה שלמה** — הכפלה ולא חלוקה.
     */
    for (const type of candidates) {
        if (affordable(ProductionKind.Unit, type, credits, myBuildings, player)) return type;
        const cost = unitDef(type).cost;
        if (credits * 3 >= cost * 2 && prerequisitesMet({ kind: ProductionKind.Unit, type }, myBuildings, player)) {
            return null;
        }
    }
    return null;
}

/**
 * מקום להצבת מבנה: ספירלה סביב המפקדה, הראשון שחוקי.
 * הסדר קבוע לחלוטין, ולכן שני מחשבים יבחרו את אותו אריח.
 */
function findSpot(
    state: GameState,
    grid: Grid,
    player: number,
    type: number,
): { x: number; y: number } | null {
    const anchor =
        state.buildings.find(
            (b) => b.owner === player && b.hp > 0 && buildingDef(b.type).id === 'yard',
        ) ?? state.buildings.find((b) => b.owner === player && b.hp > 0);
    if (!anchor) return null;

    /*
     * אותו סיבוב 180 מעלות שפריסת הפתיחה מקבלת (ראו isMirrored ב-sim/init.ts).
     * ספירלה מוחלטת הייתה מרחיבה את הבסיס של צד אחד לעבר האויב ושל השני לעבר
     * הפינה — יתרון שקט שלא קשור לאיכות המשחק.
     */
    const flip = anchor.tileX + anchor.tileY > (grid.w + grid.h) / 2 ? -1 : 1;

    // מעבר ל-8 אריחים ממילא אין כיסוי של BUILD_RADIUS, אז אין טעם להרחיב.
    for (let ring = 2; ring <= 8; ring++) {
        for (let dy = -ring; dy <= ring; dy++) {
            for (let dx = -ring; dx <= ring; dx++) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
                const x = anchor.tileX + dx * flip;
                const y = anchor.tileY + dy * flip;
                if (x < 0 || y < 0) continue;
                if (canPlace(state, grid, player, type, x, y)) return { x, y };
            }
        }
    }
    return null;
}

/** יחידת אויב שנכנסה לתחום הבסיס, אם יש. הקרובה ביותר, שוויון לפי מזהה. */
/**
 * מה שהיריב **יודע**, ולא מה שקיים.
 *
 * **זה השינוי שהופך את ערפל הקרב ממנגנון תצוגה למערכת משחק.** עד עכשיו הערפל
 * היה חד-צדדי: השחקן לא ראה את היריב, והיריב ראה את השחקן תמיד. לכן לא היה שום
 * טעם בכוח עוקף, בהסתרה או בסיור — הוא ידע איפה אתה בכל רגע.
 *
 * הסינון הוא מול `explored` ולא מול "נראה עכשיו", וזו החלטה: זיכרון של **מקום**
 * הוא ידע לגיטימי (הייתי שם, ראיתי בסיס), בעוד שרדיפה אחרי מה שנראה בדיוק ברגע
 * זה הייתה הופכת את היריב לעיוור כמעט לחלוטין ומחזירה אותו להתבצרות — מלכודת 31
 * מדלת אחורית.
 */
/**
 * מצמיד יעד לאריח **עביר** סמוך.
 *
 * ‏`isLegal` דוחה `attackMove` אל אריח לא-עביר, ובשקט: ה-AI לא מקבל שום חיווי.
 * מסוק אחד שמרחף מעל מים בתוך רדיוס ההגנה שלו היה משתק אותו לגמרי — ענף האיום
 * נכנס, דוחף פקודה שנזרקת בשער, וחוזר מיד, כלומר גם גלי ההתקפה מדולגים.
 */
function reachableTile(grid: Grid, x: number, y: number): { x: number; y: number } | null {
    if (isWalkable(grid, x, y)) return { x, y };
    // ספירלה קצרה: אם אין אריח עביר בטווח 3, אין טעם לשלוח לשם כוח קרקעי.
    for (let ring = 1; ring <= 3; ring++) {
        for (let dy = -ring; dy <= ring; dy++) {
            for (let dx = -ring; dx <= ring; dx++) {
                if (Math.abs(dx) !== ring && Math.abs(dy) !== ring) continue;
                if (isWalkable(grid, x + dx, y + dy)) return { x: x + dx, y: y + dy };
            }
        }
    }
    return null;
}

function knows(state: GameState, player: number, worldX: number, worldY: number): boolean {
    const explored = state.players[player]?.explored;
    if (!explored || explored.length === 0) return true;
    const map = getMap(state.mapId as MapId);
    const tile = Math.floor(worldY / TILE) * map.w + Math.floor(worldX / TILE);
    return explored[tile] === 1;
}

function nearestThreat(
    state: GameState,
    player: number,
    myBuildings: readonly Building[],
): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestDistance = DEFENSE_RADIUS * DEFENSE_RADIUS;
    let bestId = Number.MAX_SAFE_INTEGER;

    for (const unit of state.units) {
        if (unit.owner === player || unit.hp <= 0) continue;
        // אויב בשטח שלא נחשף אינו איום — הוא לא ידוע.
        if (!knows(state, player, unit.x, unit.y)) continue;
        for (const building of myBuildings) {
            const center = buildingCenter(building);
            const d2 = dist2(center.x, center.y, unit.x, unit.y);
            if (d2 > bestDistance) continue;
            if (d2 === bestDistance && unit.id >= bestId) continue;
            bestDistance = d2;
            bestId = unit.id;
            best = { x: Math.floor(unit.x / TILE), y: Math.floor(unit.y / TILE) };
        }
    }
    return best;
}

/**
 * כמה מבנים חיים לכל שחקן — **מבין אלה שהשחקן המדרג באמת ראה.**
 *
 * מספר המבנים ולא כוח הצבא: הוא יציב (צבא נע ונהרג בין טיקים) והוא בדיוק מה
 * שקובע כמה עוד נשאר להרוס.
 *
 * **הסינון ב-`knows` אינו קוסמטי — בלעדיו ה-AI מרמה.** הגרסה הראשונה של
 * הפונקציה ספרה את כל המבנים של כולם, והמשקל הזה מוחל גם בלולאת נקודות
 * הפתיחה — הענף שרץ דווקא כש-AI **לא** חשף כלום. התוצאה נמדדה: יריב עם אפס
 * אריחים חשופים שינה את היעד שלו בהתאם למי מהיריבים הנסתרים נחלש. זה סותר
 * ישירות את ההצהרה בראש הקובץ, ומשחקית זה אומר ששחקן שמאבד מבנים בשקט מול
 * יריב אחד מושך אליו את כל האחרים בלי שאיש סייר אליו.
 *
 * כשלא נראה כלום כל המשקלים מתאפסים ל-1, והדירוג חוזר להיות מרחק בלבד — כלומר
 * בדיוק ההתנהגות הקודמת. זו ברירת המחדל הנכונה: בלי מודיעין, תוקפים את הקרוב.
 */
function buildingStrength(state: GameState, player: number): number[] {
    const counts = new Array<number>(state.players.length).fill(0);
    for (const building of state.buildings) {
        if (building.hp <= 0) continue;
        const owner = building.owner;
        if (owner < 0 || owner >= counts.length) continue;
        const center = buildingCenter(building);
        if (!knows(state, player, center.x, center.y)) continue;
        counts[owner]!++;
    }
    return counts;
}

/**
 * לאן לתקוף: המטרה עם **הציון הנמוך ביותר**, כאשר ציון = מרחק בריבוע × כוח
 * הבעלים. אם אין מבנים ידועים — יחידה, ואז נקודת הפתיחה.
 *
 * **למה לא מרחק בלבד.** קודם נבחר תמיד המבנה הקרוב ביותר, ובמפה סימטרית זה
 * תמיד אותו שכן — לנצח. המדידה הראתה את זה במלוא חדותה: בכל 9 המשחקים שנבדקו
 * הגרף היה `1→3, 2→4, 3→2, 4→1` בדיוק, כלומר **4 זוגות מתוך 12**, ואף שחקן
 * לא תקף אף פעם את שני היריבים האחרים. "free-for-all" היה מעגל קבוע.
 *
 * הכפלה בכוח הבעלים שומרת על "קרוב עדיף" כברירת מחדל, אבל מאפשרת ליריב שנחלש
 * למשוך אליו תשומת לב גם כשהוא רחוק יותר. **מכפלת שלמים**, ולכן הדטרמיניזם נשמר.
 *
 * **וההשפעה בפועל מוגבלת, כי הכוח נספר רק ממה שנראה.** ‏`buildingStrength`
 * מסננת ב-`knows` (ראו שם למה — בלי זה ה-AI מרמה), ו-AI שלא סייר אינו יודע
 * שיריב נחלש. נמדד: עם ספירה גלויה הגענו ל-6 הדחות מ-9 משחקים ולזוגות של עד 7;
 * עם הסינון ההוגן — 2 הדחות ו-4 זוגות ברוב המשחקים, כלומר המעגל חוזר.
 * **הפתרון האמיתי הוא לתת ל-AI לסייר** (‏`hound` קיים בתוכן ואינו בשימוש), ולא
 * להרחיב את מה שהוא יודע בלי לראות.
 *
 * שבירת שוויון לפי מזהה, כדי שהבחירה תהיה זהה בכל מחשב ובכל הרצה.
 */
export function enemyTarget(
    state: GameState,
    player: number,
    myBuildings: readonly Building[],
): { x: number; y: number } | null {
    const home = myBuildings[0] ?? state.buildings.find((b) => b.owner === player);
    const origin = home ? buildingCenter(home) : { x: 0, y: 0 };
    const strength = buildingStrength(state, player);
    /** יריב בלי מבנים עדיין נספר כ-1, אחרת הציון שלו אפס והוא בולע כל בחירה. */
    const weight = (owner: number) => Math.max(1, strength[owner] ?? 1);

    let best: { x: number; y: number } | null = null;
    let bestScore = Number.MAX_SAFE_INTEGER;
    let bestId = Number.MAX_SAFE_INTEGER;

    for (const building of state.buildings) {
        if (building.owner === player || building.hp <= 0) continue;
        const center = buildingCenter(building);
        if (!knows(state, player, center.x, center.y)) continue;
        const score =
            dist2(origin.x, origin.y, center.x, center.y) *
            weight(building.owner) *
            defenceWeight(state, player, building);
        if (score > bestScore || (score === bestScore && building.id >= bestId)) continue;
        bestScore = score;
        bestId = building.id;
        best = { x: Math.floor(center.x / TILE), y: Math.floor(center.y / TILE) };
    }
    if (best) return best;

    for (const unit of state.units) {
        if (unit.owner === player || unit.hp <= 0) continue;
        if (!knows(state, player, unit.x, unit.y)) continue;
        const score = dist2(origin.x, origin.y, unit.x, unit.y) * weight(unit.owner);
        if (score > bestScore || (score === bestScore && unit.id >= bestId)) continue;
        bestScore = score;
        bestId = unit.id;
        best = { x: Math.floor(unit.x / TILE), y: Math.floor(unit.y / TILE) };
    }
    if (best) return best;

    /*
     * **לא ידוע כלום? יוצאים לסייר אל נקודת הפתיחה של היריב.**
     *
     * זה ה-fallback שמונע את הכשל של מלכודת 31 מדלת אחורית: AI שאין לו מטרה
     * מוכרת מפסיק לתקוף ומתבצר לנצח. נקודת הפתיחה היא **מידע ציבורי** בכל RTS —
     * גם שחקן אנושי יודע שהיריב במשבצת הנגדית — ולכן זה לא ריגול. ובחינם:
     * "לתקוף" הופך ל"לסייר" כשאין מה לתקוף.
     */
    /*
     * **היריב הקרוב ביותר, ולא "הראשון שאינו אני".**
     *
     * במשחק של שניים זה אותו דבר; בארבעה זה ההבדל בין free-for-all לבין ארבעה
     * מחשבים שכולם צועדים לאותה פינה. מי ששכן לך הוא מי שיפגוש אותך ראשון —
     * וזה מה שגורם לחלק מהיריבים להילחם זה בזה במקום רק בך.
     */
    /*
     * הדירוג הוא **גיאומטרי ולא לפי אינדקס**: `startRotation` מחזירה כמה רבעי
     * סיבוב מפרידים בין משבצת לראשונה, כלומר "השכן הבא עם כיוון השעון".
     *
     * סדר המשבצות בתוכן הוא [אלכסון, נגדי, ...] ולא סדר הטבעת — חוזה שנדרש
     * לטסט ההוגנות של שני שחקנים — ולכן שבירת שוויון לפי אינדקס נתנה לשני
     * יריבים את אותו קורבן.
     */
    /*
     * **נקודות הפתיחה מגיעות מהמצב ולא מהמפה** — הן מוגרלות בכל משחק
     * (`sim/starts.ts`), ומספר שנקרא מהמפה הכתובה היה שולח את הגלים אל פינה
     * שאיש אינו יושב בה.
     */
    const size = getMap(state.mapId as MapId).w;
    const slots = state.startSlots;
    const starts = { w: size, h: size, startSlots: slots };
    const myTurn = Math.max(0, startRotation(starts, player));
    const mine = slots[player];
    let nearest: { x: number; y: number } | null = null;
    let nearestDistance = Number.MAX_SAFE_INTEGER;
    let nearestRank = Number.MAX_SAFE_INTEGER;
    for (const [index, slot] of slots.entries()) {
        if (index === player) continue;
        // שחקן שכבר הובס אינו יעד — אחרת הגלים ממשיכים אל בסיס שאיננו.
        if (state.players[index]?.defeated) continue;
        const dx = (mine?.x ?? 0) - slot.x;
        const dy = (mine?.y ?? 0) - slot.y;
        /*
         * **אותו שקלול חולשה כמו למעלה, וכאן הוא זה שבאמת משנה.**
         *
         * ה-AI מכיר בפועל רק את מה שחשף, והוא חושף רק את הכיוון שאליו הוא שולח
         * גלים — ולכן רוב הזמן הוא כאן ולא בלולאת המבנים הידועים. שקלול שחל רק
         * שם נמדד כחסר השפעה מוחלטת: התוצאות יצאו זהות ביט-לביט.
         *
         * המרחק כאן באריחים ולא ביחידות עולם, וזה בסדר — ההשוואה היא בין
         * נקודות פתיחה בלבד, כולן באותה יחידה.
         */
        const d2 = (dx * dx + dy * dy) * Math.max(1, strength[index] ?? 1);
        /*
         * **שבירת שוויון סיבובית, לא לפי אינדקס.**
         *
         * בארבע פינות לכל שחקן יש בדיוק שני שכנים במרחק זהה, ו"האינדקס הנמוך
         * מנצח" נתן לכל ארבעת היריבים את אותו גרף התקפה: עמדות 1 ו-3 הותקפו כל
         * אחת ע"י **שניים**, ועמדות 2 ו-4 ע"י **אף אחד**. זה נמדד — 83% הדחה
         * לעמדה 1 מול 0% לעמדה 2 — כלומר המפה הייתה הוגנת והיריב לא.
         *
         * המרחק **מהשחקן במחזור** נותן לכל אחד שכן אחר: 0→1, 1→2, 2→3, 3→0.
         */
        const rank = (startRotation(starts, index) - myTurn + 4) % 4;
        if (d2 < nearestDistance || (d2 === nearestDistance && rank < nearestRank)) {
            nearestDistance = d2;
            nearestRank = rank;
            nearest = { x: slot.x, y: slot.y };
        }
    }
    return nearest;
}

/** מרחק שבתוכו מגדל נחשב "מגן" על המבנה, ביחידות עולם. */
const DEFENCE_COVER = 9 * TILE;

/**
 * כמה יקר לתקוף דווקא כאן — **מספר שלם, ומוכפל בציון.**
 *
 * ‏`attackMove` אל המבנה הקרוב ביותר פירושו לצעוד ישר אל הפילבוקס: הפרימטר
 * הוא בהגדרה מה שקרוב. נמדד שהתוקף מפסיד — יריב שתקף יותר סיים עם פחות
 * יחידות חיות מיריב שישב בבית, בכל הרמות ובכל המפות.
 *
 * המשקל סופר **רק מגדלים שהתוקף ראה** (`knows`), בדיוק כמו ספירת הכוח שמעליו:
 * דירוג שמשתמש במידע שהשחקן לא ראה הוא AI שמרמה, גם כשהוא "רק" משקל
 * (מלכודת 66).
 */
function defenceWeight(state: GameState, player: number, target: Building): number {
    const center = buildingCenter(target);
    let guards = 0;
    for (const building of state.buildings) {
        if (building.owner === player || building.hp <= 0) continue;
        if (buildingDef(building.type).weapon === null) continue;
        const spot = buildingCenter(building);
        if (!knows(state, player, spot.x, spot.y)) continue;
        if (dist2(center.x, center.y, spot.x, spot.y) <= DEFENCE_COVER * DEFENCE_COVER) guards++;
    }
    return 1 + guards;
}

function buildingCenter(building: Building): { x: number; y: number } {
    const def = buildingDef(building.type);
    return {
        x: building.tileX * TILE + (def.w * TILE) / 2,
        y: building.tileY * TILE + (def.h * TILE) / 2,
    };
}
